import React, { useEffect, useMemo } from 'react';
import { InputControl, SelectControl, Row, Col } from '@design-system-etat/dsfr-react';
import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getAPI, reinitialiseAPI } from 'store/apiReducer';
import { useFormContext } from 'react-hook-form';
import {
    formaterOptions,
    getMessageErreurChampManquant,
    verifierSiDepartementEstOuvertALaCreationDeCompte,
} from '../../../utils/utils';
import { REGEXP_CODE_POSTAL } from '../../../constants/contraintes.constants';
import usagerClientReducer from '../../../store/usagerReducer';
import {
    API_RECUPERER_ADRESSE_LISTE_EXTENSION,
    CONTEXT_API_RECUPERER_ADRESSE_LISTE_EXTENSION,
} from '../../../constants/apiUrls.constants';

const FormulaireAdresseSimple = ({
    getApi,
    reinitialiseApi,
    isLoadingListeExtension,
    dataListeExtension,
    errorListeExtension,
}) => {
    const { register, formState, watch, trigger } = useFormContext();

    useEffect(() => {
        getApi(
            usagerClientReducer,
            API_RECUPERER_ADRESSE_LISTE_EXTENSION,
            CONTEXT_API_RECUPERER_ADRESSE_LISTE_EXTENSION,
        );

        return () => {
            reinitialiseApi(API_RECUPERER_ADRESSE_LISTE_EXTENSION);
        };
    }, []);

    useEffect(() => {
        if (formState.isDirty) {
            trigger(['typeEtNomDeVoie', 'lieuDit']);
        }
    }, [watch('typeEtNomDeVoie'), watch('lieuDit')]);

    const extensionsAdresse = useMemo(() => {
        const extensionsAdresseVide = [{ key: '', value: '', libelle: '' }];

        if (!isLoadingListeExtension && !errorListeExtension && dataListeExtension) {
            const formaterExtension = dataListeExtension.map((extension) => {
                return { key: extension, value: extension, libelle: extension };
            });

            return extensionsAdresseVide.concat(formaterExtension);
        }

        return extensionsAdresseVide;
    }, [isLoadingListeExtension, dataListeExtension, errorListeExtension]);

    return (
        <>
            <Row gutters>
                <Col col={12} lg={6}>
                    <InputControl
                        autocomplete="address-line1"
                        label="Numéro de voie"
                        type="text"
                        maxLength="10"
                        id="numeroDeVoie"
                        {...register('numeroDeVoie')}
                        uppercase
                    />
                </Col>
                <Col col={12} lg={6}>
                    <SelectControl
                        id="extension"
                        name="extension"
                        label="Extension"
                        uppercase
                        options={formaterOptions(extensionsAdresse)}
                        {...register('extension')}
                    />
                </Col>
            </Row>
            <Row gutters>
                <Col col={12} lg={6}>
                    <InputControl
                        autocomplete="address-line2"
                        label="Type et nom de voie *"
                        type="text"
                        maxLength="60"
                        uppercase
                        id="typeEtNomDeVoie"
                        {...register('typeEtNomDeVoie', {
                            required: {
                                value: !watch('lieuDit'),
                                message: getMessageErreurChampManquant('Type et nom de voie'),
                            },
                        })}
                        state={formState.errors?.typeEtNomDeVoie ? 'error' : null}
                        message={formState.errors?.typeEtNomDeVoie?.message}
                    />
                </Col>
                <Col col={12} lg={6}>
                    <InputControl
                        autocomplete="address-line3"
                        label="Lieu-dit"
                        type="text"
                        maxLength="60"
                        uppercase
                        id="lieuDit"
                        {...register('lieuDit')}
                    />
                </Col>
            </Row>
            <Row gutters verticalAlign="bottom">
                <Col col={12} lg={6}>
                    <InputControl
                        label="Code postal *"
                        hintText="Exemple : 77610"
                        required
                        type="text"
                        maxLength="5"
                        minLength="5"
                        id="codePostal"
                        {...register('codePostal', {
                            required: getMessageErreurChampManquant('Code postal'),
                            pattern: {
                                value: REGEXP_CODE_POSTAL,
                                message:
                                    "Le format du code postal n'est pas valide ( Exemple: 77610 )",
                            },
                            validate: (value) =>
                                verifierSiDepartementEstOuvertALaCreationDeCompte(value),
                        })}
                        state={formState.errors?.codePostal ? 'error' : null}
                        message={formState.errors?.codePostal?.message}
                    />
                </Col>
                <Col col={12} lg={6}>
                    <InputControl
                        label="Ville *"
                        required
                        type="text"
                        maxLength="60"
                        uppercase
                        id="ville"
                        {...register('ville', {
                            required: getMessageErreurChampManquant('Ville'),
                        })}
                        state={formState.errors?.ville ? 'error' : null}
                        message={formState.errors?.ville?.message}
                    />
                </Col>
            </Row>
        </>
    );
};

FormulaireAdresseSimple.propTypes = {
    getApi: PropTypes.func.isRequired,
    reinitialiseApi: PropTypes.func.isRequired,
    hookForm: PropTypes.shape({
        register: PropTypes.func.isRequired,
        formState: PropTypes.shape({
            isDirty: PropTypes.bool.isRequired,
            errors: PropTypes.shape({
                typeEtNomDeVoie: PropTypes.any,
                codePostal: PropTypes.any,
                ville: PropTypes.any,
            }),
        }),
        watch: PropTypes.func.isRequired,
        trigger: PropTypes.func.isRequired,
        getValues: PropTypes.func.isRequired,
    }).isRequired,
    isLoadingListeExtension: PropTypes.bool,
    dataListeExtension: PropTypes.bool,
    errorListeExtension: PropTypes.bool,
};

export default connect(
    (state) => {
        const {
            isLoading: isLoadingListeExtension,
            data: dataListeExtension,
            error: errorListeExtension,
        } = state.adresseExtensions.recupererListe;
        return {
            isLoadingListeExtension,
            dataListeExtension,
            errorListeExtension,
        };
    },
    {
        getApi: getAPI,
        reinitialiseApi: reinitialiseAPI,
    },
)(FormulaireAdresseSimple);
